import { useEffect, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import { captureSvgAsPng } from "../../utils/svgToImage";
import { useParams, useNavigate } from "react-router";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import Checkbox from "../../components/form/input/Checkbox";
import AbbreviationTooltip from "../../components/ui/AbbreviationTooltip";
import {
  fetchPlan,
  PlanResponse,
  getPlanStatus,
  movePlanToReview,
  approvePlan,
  filePlan,
  PlanStatusResponse,
  PlanApprovalStatus
} from "../../lib/api/plans";
import ProposedPlugDiagram, { transformPlanToProposedDiagramData, ProposedPlugWBDHandle } from "../../components/diagrams/ProposedPlugWBD";
import ChatBox from "../../components/chats/ChatBox";
import { createChatThread, listChatThreads, ChatThread } from "../../lib/api/chat";
import { useToast } from "../../contexts/ToastContext";
import JurisdictionBadge from "../../components/ui/JurisdictionBadge";

export default function PlanDetails() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"diagram" | "details" | "chat">("diagram");
  const [chatThread, setChatThread] = useState<ChatThread | null>(null);
  const [planStatus, setPlanStatus] = useState<PlanStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [jsonExpanded, setJsonExpanded] = useState(false);

  const wbdRef = useRef<ProposedPlugWBDHandle>(null);

  const handleExportPDF = async () => {
    const svgEl = wbdRef.current?.getSvgElement()
    if (!svgEl) return

    try {
      const doc = new jsPDF('portrait', 'mm', 'letter')
      const pageWidth = doc.internal.pageSize.getWidth()
      let y = 15

      // ── Page 1: Well Info + Tables ──────────────────────────────────────

      // Title
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Well Bore Diagram', pageWidth / 2, y, { align: 'center' })
      y += 8

      // Well header info
      const w = plan?.well
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.text(`API: ${w?.api14 ?? ''}`, 15, y)
      doc.text(`Operator: ${w?.operator_name ?? ''}`, pageWidth / 2, y)
      y += 5
      doc.text(`Lease: ${w?.lease_name ?? ''}  Well #: ${w?.well_number ?? ''}`, 15, y)
      doc.text(`Field: ${w?.field_name ?? ''}`, pageWidth / 2, y)
      y += 10

      // ── Draw table helper ───────────────────────────────────────────────
      const drawTable = (
        headers: string[],
        rows: string[][],
        startY: number,
        colWidths: number[]
      ): number => {
        const rowHeight = 6
        const startX = 15
        const tableWidth = colWidths.reduce((a, b) => a + b, 0)
        let currentY = startY

        // Header row background
        doc.setFillColor(220, 230, 242)
        doc.rect(startX, currentY, tableWidth, rowHeight, 'F')

        // Header text
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(9)
        let x = startX
        headers.forEach((header, i) => {
          doc.text(header, x + 2, currentY + 4)
          x += colWidths[i]
        })
        currentY += rowHeight

        // Data rows
        doc.setFont('helvetica', 'normal')
        rows.forEach((row, rowIdx) => {
          // Alternating row shading
          if (rowIdx % 2 === 0) {
            doc.setFillColor(248, 250, 252)
            doc.rect(startX, currentY, tableWidth, rowHeight, 'F')
          }
          x = startX
          row.forEach((cell, i) => {
            doc.text(String(cell), x + 2, currentY + 4)
            x += colWidths[i]
          })
          currentY += rowHeight
        })

        // Outer border
        doc.setDrawColor(180, 180, 180)
        doc.setLineWidth(0.3)
        doc.rect(startX, startY, tableWidth, currentY - startY)

        // Horizontal lines
        let lineY = startY + rowHeight
        for (let r = 0; r <= rows.length; r++) {
          doc.line(startX, lineY, startX + tableWidth, lineY)
          lineY += rowHeight
        }

        // Vertical lines
        x = startX
        colWidths.forEach((cw) => {
          x += cw
          doc.line(x, startY, x, currentY)
        })

        return currentY + 4
      }

      // ── Casing Record Table ─────────────────────────────────────────────
      const wgRaw = plan?.well_geometry as unknown as Record<string, unknown>
      const casingStrings: Record<string, unknown>[] = [
        ...((wgRaw?.casing_strings as Record<string, unknown>[] || [])),
        ...((wgRaw?.liner as Record<string, unknown>[] || []).map((l) => ({ ...l, string: 'Liner' }))),
      ]
      const casingRows: string[][] = casingStrings.map((c) => [
        String(c.string || c.casing_type || c.string_type || ''),
        String(c.size_in ?? ''),
        String(c.weight_ppf ? `${c.weight_ppf}` : ''),
        String(c.top_ft ?? 0),
        String(c.bottom_ft ?? c.shoe_depth_ft ?? ''),
        String(c.hole_size_in ?? ''),
      ])

      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Casing Record', 15, y)
      y += 5

      if (casingRows.length > 0) {
        y = drawTable(
          ['String Type', 'Size (in)', 'Weight (ppf)', 'Top (ft)', 'Bottom (ft)', 'Hole Size (in)'],
          casingRows,
          y,
          [40, 25, 28, 25, 30, 32]
        )
      } else {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(140, 140, 140)
        doc.text('No casing data available.', 15, y + 4)
        doc.setTextColor(0, 0, 0)
        y += 10
      }

      y += 4

      // ── Formation Tops Table ────────────────────────────────────────────
      const formations: Record<string, unknown>[] = [...((plan?.well_geometry as unknown as Record<string, unknown>)?.formation_tops as Record<string, unknown>[] || [])].sort((a, b) => (Number(b.top_ft) || 0) - (Number(a.top_ft) || 0))
      const formationRows: string[][] = formations.map((f) => [
        String(f.formation || ''),
        String(f.top_ft ?? ''),
      ])

      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('Formation Tops', 15, y)
      y += 5

      if (formationRows.length > 0) {
        y = drawTable(
          ['Formation', 'Top (ft)'],
          formationRows,
          y,
          [100, 50]
        )
      } else {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(140, 140, 140)
        doc.text('No formation tops data available.', 15, y + 4)
        doc.setTextColor(0, 0, 0)
        y += 10
      }

      // ── Page 2: WBD Diagram ─────────────────────────────────────────────
      const pngBlob = await captureSvgAsPng(svgEl, 2)
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(pngBlob)
      })

      doc.addPage()
      const imgWidth = pageWidth - 20
      const bbox = svgEl.getBoundingClientRect()
      const svgAspect = bbox.height > 0 && bbox.width > 0 ? bbox.height / bbox.width : 1.5
      const imgHeight = imgWidth * svgAspect
      const pageHeight = doc.internal.pageSize.getHeight()
      doc.addImage(dataUrl, 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20))

      // ── Save ────────────────────────────────────────────────────────────
      doc.save(`WBD_${plan?.well?.api14 || 'plan'}.pdf`)
    } catch (err) {
      console.error('Failed to export WBD:', err)
    }
  };

  // Visibility toggles for diagram components
  const [visibility, setVisibility] = useState({
    casing: true,
    tubing: true,
    perforations: true,
    formationTops: true,
    newCementPlugs: true,
    existingCement: true,
    bridgePlugs: true,
    existingTools: true,
  });

  const handleVisibilityToggle = (key: keyof typeof visibility, checked: boolean) => {
    setVisibility((prev) => ({ ...prev, [key]: checked }));
  };

  useEffect(() => {
    const loadPlan = async () => {
      if (!planId) {
        setError("Plan ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log("[PlanDetails] Loading plan:", planId);

        // Fetch the plan directly using the plan ID
        const planData = await fetchPlan(planId);
        setPlan(planData);

        // Fetch plan status
        try {
          const statusData = await getPlanStatus(planId);
          setPlanStatus(statusData);
        } catch (statusErr) {
          console.warn("[PlanDetails] Could not fetch plan status:", statusErr);
          setStatusError(
            statusErr instanceof Error ? statusErr.message : "Failed to load plan status"
          );
        }

        // Initialize chat thread for this plan if on chat tab
        try {
          const threads = await listChatThreads();
          // Look for existing thread for this plan
          const existingThread = threads.find(
            (t) => t.baseline_plan_id === planData.plan_id || t.current_plan_id === planData.plan_id
          );

          if (existingThread) {
            setChatThread(existingThread);
          } else {
            // Create new thread if none exists
            const newThread = await createChatThread({
              well_api14: planData.well.api14,
              plan_id: planData.plan_id,
              title: `${planData.well.lease_name} #${planData.well.well_number} - Plan Discussion`,
            });
            setChatThread(newThread);
          }
        } catch (chatErr) {
          console.warn("[PlanDetails] Could not initialize chat thread:", chatErr);
        }
      } catch (err) {
        console.error("[PlanDetails] Error loading plan:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load plan. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    loadPlan();
  }, [planId]);

  const handleCopySection = (content: string, section: string) => {
    navigator.clipboard.writeText(content);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const getStatusBadgeColor = (status: PlanApprovalStatus): string => {
    const colors: Record<PlanApprovalStatus, string> = {
      draft: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300",
      internal_review: "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300",
      engineer_approved: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",
      filed: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
      under_agency_review: "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300",
      agency_approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
      agency_rejected: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
      revision_requested: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
    };
    return colors[status] || colors.draft;
  };

  const getStatusLabel = (status: PlanApprovalStatus): string => {
    const labels: Record<PlanApprovalStatus, string> = {
      draft: "Draft",
      internal_review: "Internal Review",
      engineer_approved: "Engineer Approved",
      filed: "Filed",
      under_agency_review: "Under Agency Review",
      agency_approved: "Agency Approved",
      agency_rejected: "Agency Rejected",
      revision_requested: "Revision Requested",
    };
    return labels[status] || status;
  };

  const handleStatusAction = async (action: "review" | "approve" | "file") => {
    if (!planId) return;

    setStatusLoading(true);

    try {
      let response;
      switch (action) {
        case "review":
          response = await movePlanToReview(planId);
          success('Plan Status Updated', response.message || 'Plan moved to review successfully.');
          break;
        case "approve":
          response = await approvePlan(planId);
          success('Plan Approved', response.message || 'The plan has been approved successfully.');
          break;
        case "file":
          response = await filePlan(planId);
          success('Plan Filed', response.message || 'The plan has been filed with the agency successfully.');
          break;
      }

      // Refresh plan status
      const updatedStatus = await getPlanStatus(planId);
      setPlanStatus(updatedStatus);
    } catch (err) {
      console.error("[PlanDetails] Status action error:", err);
      showError('Action Failed', err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const refreshPlan = async () => {
    if (!chatThread) return;
    try {
      console.log("[PlanDetails] Refreshing chat thread after modification");
      
      // Fetch the latest chat thread to get the updated current_plan
      const { getChatThread } = await import("../../lib/api/chat");
      const updatedThread = await getChatThread(chatThread.id);
      
      console.log("[PlanDetails] Thread current_plan_id:", updatedThread.current_plan_id);
      console.log("[PlanDetails] Original plan_id:", planId);
      
      // If current_plan_id changed, it means a modification was applied
      if (updatedThread.current_plan_id && updatedThread.current_plan_id !== chatThread.current_plan_id) {
        console.log("[PlanDetails] ✅ Plan was modified! Fetching new plan:", updatedThread.current_plan_id);
        const newPlan = await fetchPlan(updatedThread.current_plan_id, true);
        setPlan(newPlan);
        
        const oldStepCount = plan?.payload?.steps?.length || 0;
        const newStepCount = newPlan.payload?.steps?.length || 0;
        
        console.log(`[PlanDetails] ✅ Plan refreshed! Steps changed: ${oldStepCount} → ${newStepCount}`);
        return;
      }
      
      // Otherwise just refresh the current plan with cache bust to check for any changes
      if (planId) {
        const updatedPlan = await fetchPlan(planId, true);
        
        const oldStepCount = plan?.payload?.steps?.length || 0;
        const newStepCount = updatedPlan.payload?.steps?.length || 0;
        
        if (newStepCount !== oldStepCount) {
          console.log(`[PlanDetails] ✅ Plan changed: ${oldStepCount} → ${newStepCount} steps`);
          setPlan(updatedPlan);
        } else {
          console.log("[PlanDetails] No plan changes detected (same step count)");
        }
      }
    } catch (err) {
      console.error("[PlanDetails] Error refreshing plan:", err);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb pageTitle="Plugging Plan" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              Loading plan...
            </p>
          </div>
        </div>
      </>
    );
  }

  if (error || !plan) {
    return (
      <>
        <PageBreadcrumb pageTitle="Plugging Plan" />
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="p-6">
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">
                {error || "Plan not found"}
              </p>
              <Button
                onClick={() => navigate("/plan-generation")}
                className="mt-4"
                variant="outline"
              >
                Generate New Plan
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const payload = plan.payload;
  const well = plan.well;

  return (
    <>
      <PageMeta
        title="Plugging Plan Details | RegulAgent"
        description="View and manage generated plugging plans"
      />

      <PageBreadcrumb pageTitle="Plugging Plan Details" />

      <div>

      {/* Jurisdiction badge — always visible once plan loads */}
      <div className="mb-4 -mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <JurisdictionBadge
            jurisdiction={
              (payload.jurisdiction?.toUpperCase() === "NM" ? "NM" : "TX") as "TX" | "NM"
            }
            formType={
              payload.form_type === "c_103" || payload.jurisdiction?.toUpperCase() === "NM"
                ? "c103"
                : "w3a"
            }
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {well.api14}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleExportPDF}
          className="export-pdf-btn flex items-center gap-1.5"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export WBD
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-white/[0.05]">
        <button
          onClick={() => setActiveTab("diagram")}
          className={`px-4 py-3 font-medium text-sm transition-colors ${
            activeTab === "diagram"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          <AbbreviationTooltip term="WBD" /> (Wellbore Diagram)
        </button>
        <button
          onClick={() => setActiveTab("details")}
          className={`px-4 py-3 font-medium text-sm transition-colors ${
            activeTab === "details"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          Plan Details
        </button>
        <button
          onClick={() => setActiveTab("chat")}
          className={`px-4 py-3 font-medium text-sm transition-colors ${
            activeTab === "chat"
              ? "border-b-2 border-brand-500 text-brand-600 dark:text-brand-400"
              : "text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          AI Assistant
        </button>
      </div>

      {/* Plan Approval Workflow */}
      {planStatus && (
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Plan Approval Status
              </h3>
              <span
                className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(
                  planStatus.current_status
                )}`}
              >
                {getStatusLabel(planStatus.current_status)}
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Action Buttons */}
            <div className="mb-6">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
                Available Actions
              </p>
              <div className="flex flex-wrap gap-3">
                {planStatus.current_status === "draft" && (
                  <Button
                    onClick={() => handleStatusAction("review")}
                    disabled={statusLoading}
                    size="sm"
                  >
                    {statusLoading ? "Processing..." : "Submit for Review"}
                  </Button>
                )}

                {planStatus.current_status === "internal_review" && (
                  <>
                    <Button
                      onClick={() => handleStatusAction("approve")}
                      disabled={statusLoading}
                      size="sm"
                    >
                      {statusLoading ? "Processing..." : "Approve"}
                    </Button>
                    <Button
                      onClick={() => handleStatusAction("review")}
                      disabled={statusLoading}
                      variant="outline"
                      size="sm"
                    >
                      {statusLoading ? "Processing..." : "Back to Draft"}
                    </Button>
                  </>
                )}

                {planStatus.current_status === "engineer_approved" && (
                  <Button
                    onClick={() => handleStatusAction("file")}
                    disabled={statusLoading}
                    size="sm"
                  >
                    {statusLoading ? "Processing..." : "File with Agency"}
                  </Button>
                )}

                {(planStatus.current_status === "filed" ||
                  planStatus.current_status === "under_agency_review" ||
                  planStatus.current_status === "agency_approved" ||
                  planStatus.current_status === "agency_rejected") && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No actions available. Plan is in final state.
                  </p>
                )}
              </div>
            </div>

            {/* Status History */}
            {planStatus.history && planStatus.history.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
                  Status History
                </p>
                <div className="space-y-3">
                  {planStatus.history.map((change, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 border-l-2 border-gray-200 pl-4 dark:border-gray-700"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(
                              change.status
                            )}`}
                          >
                            {getStatusLabel(change.status)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(change.changed_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                          Changed by: {change.changed_by}
                        </p>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {change.change_type === "+" && "Created"}
                        {change.change_type === "~" && "Updated"}
                        {change.change_type === "-" && "Deleted"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status Loading/Error States */}
      {!planStatus && statusError && (
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="p-6">
            <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-500/15">
              <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                Could not load approval status: {statusError}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Diagram Tab — always rendered; hidden when another tab is active, but shown in print */}
      {plan && (
        <div className={`space-y-4 ${activeTab === "diagram" ? "block" : "hidden"}`}>
          {/* Visibility Controls */}
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Diagram Visibility Controls
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: "casing" as const, label: "Casing Strings" },
                  { key: "tubing" as const, label: "Tubing" },
                  { key: "perforations" as const, label: "Perforations" },
                  { key: "formationTops" as const, label: "Formation Tops" },
                  { key: "newCementPlugs" as const, label: "New Cement Plugs" },
                  { key: "existingCement" as const, label: "Existing Cement" },
                  { key: "bridgePlugs" as const, label: "Bridge Plugs (CIBP)" },
                  { key: "existingTools" as const, label: "Existing Tools" },
                ].map((item) => (
                  <div
                    key={item.key}
                    className="hover:bg-gray-50 dark:hover:bg-white/[0.02] p-2 rounded-lg transition-colors"
                  >
                    <Checkbox
                      checked={visibility[item.key]}
                      onChange={(checked) => handleVisibilityToggle(item.key, checked)}
                      label={item.label}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Casing Record & Formation Tops */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Casing Record */}
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  Casing Record
                </h3>
              </div>
              <div className="p-4 overflow-x-auto">
                {[...(plan.well_geometry?.casing_strings || []), ...(plan.well_geometry?.liner || []).map((l: Record<string, unknown>) => ({ ...l, string: 'Liner' }))].length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No casing data available.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-white/[0.03]">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">String Type</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Size (in)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Weight (ppf)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Top (ft)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Bottom/Shoe (ft)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Hole Size (in)</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Cement Top (ft)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                      {[...(plan.well_geometry?.casing_strings || []), ...(plan.well_geometry?.liner || []).map((l: Record<string, unknown>) => ({ ...l, string: 'Liner' }))].map((c: Record<string, unknown>, idx: number) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                          <td className="px-3 py-2 text-gray-800 dark:text-white/80">
                            {(c.string || c.casing_type || c.string_type || "—") as string}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {c.size_in != null ? String(c.size_in) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {c.weight_ppf != null ? String(c.weight_ppf) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {c.top_ft != null ? String(c.top_ft) : "0"}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {c.bottom_ft != null ? String(c.bottom_ft) : c.shoe_depth_ft != null ? String(c.shoe_depth_ft) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {c.hole_size_in != null ? String(c.hole_size_in) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {c.cement_top_ft != null ? String(c.cement_top_ft) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Formation Tops */}
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  Formation Tops
                </h3>
              </div>
              <div className="p-4 overflow-x-auto">
                {(plan.well_geometry?.formation_tops || []).length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">No formation tops data available.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-white/[0.03]">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Formation</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Top (ft)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                      {[...(plan.well_geometry?.formation_tops || [])].sort((a, b) => (b.top_ft || 0) - (a.top_ft || 0)).map((f, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
                          <td className="px-3 py-2 text-gray-800 dark:text-white/80">
                            {(f.formation || "—") as string}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                            {f.top_ft != null ? String(f.top_ft) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Diagram */}
          <div className="bg-gray-50 rounded-2xl">
            <ProposedPlugDiagram
              ref={wbdRef}
              data={transformPlanToProposedDiagramData(plan)}
              width={800}
              height={1200}
              visibility={visibility}
            />
          </div>
        </div>
      )}

      {/* Chat Tab */}
      {activeTab === "chat" && (
        <div className="h-[600px]">
          {chatThread ? (
            <ChatBox thread={chatThread} onPlanModified={refreshPlan} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent mb-3"></div>
                <p className="text-gray-600 dark:text-gray-400">
                  Initializing AI Assistant...
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Details Tab */}
      <div className={`space-y-6 ${activeTab === "details" ? "block" : "hidden"}`}>
        {/* Well Information Card */}
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Well Information
            </h3>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { label: "API Number", value: well.api14 },
                { label: "Operator", value: well.operator_name },
                { label: "Lease", value: well.lease_name },
                { label: "Well Number", value: well.well_number },
                { label: "Field", value: well.field_name },
                { label: "County", value: well.county },
                { label: "State", value: well.state },
              ].map((item) => (
                <div key={item.label}>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-medium text-gray-800 dark:text-white/90">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Plan Overview */}
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Plan Overview
            </h3>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  label: "Total Sacks",
                  value: payload.materials_totals?.total_sacks || "0",
                },
                {
                  label: "Total Barrels",
                  value: (payload.materials_totals?.total_bbl || 0).toFixed(2),
                },
                {
                  label: "Total Steps",
                  value: payload.steps?.length || "0",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-lg bg-gray-50 p-4 dark:bg-white/[0.02]"
                >
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    {item.label}
                  </p>
                  <p className="mt-2 text-2xl font-bold text-brand-600 dark:text-brand-400">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>

            {payload.formations_targeted && payload.formations_targeted.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                  Formations Targeted
                </p>
                <div className="flex flex-wrap gap-2">
                  {payload.formations_targeted.map((formation, idx) => (
                    <span
                      key={idx}
                      className="inline-block px-3 py-1 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 text-xs font-medium"
                    >
                      {formation}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Plugging Steps */}
        {payload.steps && payload.steps.length > 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Plugging Steps ({payload.steps.length})
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {payload.steps.map((step, idx) => {
                // TX W-3A and NM C-103 PlanSnapshots store steps under different field
                // names. Normalize both shapes so this card renders for either.
                const stepType = step.type ?? step.step_type ?? "";
                const stepTypeLabel = stepType.replace(/_/g, " ") || "—";
                const topFt = step.top_ft ?? step.depth_top_ft ?? null;
                const bottomFt = step.bottom_ft ?? step.depth_bottom_ft ?? null;
                const formationLabel =
                  step.formation ||
                  (Array.isArray(step.formations_referenced) && step.formations_referenced.length > 0
                    ? step.formations_referenced.join(", ")
                    : null);
                const instructions = step.special_instructions ?? step.description ?? null;
                // Canonical type is string[], but NM C-103 stores a single string at runtime.
                const rawBasis = step.regulatory_basis as unknown as string[] | string | null | undefined;
                const regBasis = Array.isArray(rawBasis) ? rawBasis : rawBasis ? [rawBasis] : [];
                return (
                <div
                  key={idx}
                  className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800 dark:text-white/90">
                        Step {idx + 1}:{" "}
                        {formationLabel
                          ? `${formationLabel} ${stepTypeLabel}`
                          : stepTypeLabel}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Depth: {topFt ?? "—"}' - {bottomFt ?? "—"}' | Sacks: {step.sacks || "—"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="inline-block px-2 py-1 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300 text-xs font-medium">
                        {stepTypeLabel}
                      </span>
                      {formationLabel && (
                        <span className="inline-block px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 text-xs font-medium">
                          {formationLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  {step.cement_class && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      <strong>Cement Class:</strong> {step.cement_class}
                    </p>
                  )}

                  {instructions && (
                    <div className="bg-yellow-50 dark:bg-yellow-500/10 rounded p-2 mb-2">
                      <p className="text-xs text-yellow-800 dark:text-yellow-300">
                        <strong>Instructions:</strong> {instructions}
                      </p>
                    </div>
                  )}

                  {regBasis.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Regulatory Basis:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {regBasis.map((basis, bidx) => (
                          <span
                            key={bidx}
                            className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-gray-700 dark:text-gray-300"
                          >
                            {basis}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Plan JSON/Raw Data */}
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Raw Plan Data
              </h3>
              <button
                onClick={() => setJsonExpanded((prev) => !prev)}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                {jsonExpanded ? "Hide Raw JSON ▴" : "Show Raw JSON ▾"}
              </button>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                handleCopySection(JSON.stringify(payload, null, 2), "json");
              }}
            >
              {copiedSection === "json" ? "Copied!" : "Copy JSON"}
            </Button>
          </div>

          {jsonExpanded && (
            <div className="p-6">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                <code>{JSON.stringify(payload, null, 2)}</code>
              </pre>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-4 dark:border-white/[0.05] dark:bg-white/[0.03] flex gap-3">
          <Button onClick={() => navigate("/plan-generation")}>
            Create Another Plan
          </Button>
          <Button variant="outline" onClick={() => navigate("/well-registry")}>
            View Well Registry
          </Button>
        </div>
      </div>
      </div>{/* end plan-print-content */}
    </>
  );
}
